import i18n from "i18next";
import { useCallback, useRef } from "react";
import { useToast } from "@/components/shared/Toast";
import { useSharingApi } from "@/hooks/useSharingApi";
import type { RemoveChoice } from "./RemoveAccessDialog";

/** The Undo window (Olivia, 10 Oct): the notice, if any, waits for it to close. */
export const UNDO_MS = 6000;

/**
 * Remove at once, offer Undo for six seconds, and only then tell the person (if
 * the owner chose to), so an undone removal never notifies anyone.
 */
export function useRemoveWithUndo(documentId: string, onChange: () => void) {
  const sharing = useSharingApi();
  const { showToast } = useToast();
  const timers = useRef(new Map<string, number>());

  return useCallback(
    async (person: { userId: string; name: string; role: string }, choice: RemoveChoice) => {
      await sharing.remove(documentId, person.userId);
      onChange();
      let undone = false;
      const notice = window.setTimeout(() => {
        timers.current.delete(person.userId);
        if (!undone && choice.notify) void sharing.sendRemovalNotice(documentId, person.userId, choice.allowReply).catch(() => {});
      }, UNDO_MS);
      timers.current.set(person.userId, notice);
      showToast(i18n.t("sharing:remove.noLongerHasAccess", { name: person.name }), "info", {
        durationMs: UNDO_MS,
        action: {
          key: "undo-remove",
          label: i18n.t("sharing:remove.undo"),
          onClick: () => {
            undone = true;
            window.clearTimeout(notice);
            timers.current.delete(person.userId);
            void sharing.restore(documentId, person.userId, person.role).then(onChange, () => {
              showToast(i18n.t("sharing:remove.couldNotRestore", { name: person.name }), "error");
            });
          },
        },
      });
    },
    [documentId, onChange, sharing, showToast],
  );
}
